/**
 * PreviewPanel Component
 * Full-featured preview that replicates the exact user Dashboard experience
 * Shows all filters, search, survey popup, and layout exactly as end users will see
 * Feature: User Management (002-user-management) - Phase 4 US3 - T088
 */

import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import AdminApiService from '../services/AdminApiService.js';
import ApiService from '../services/ApiService.js';
import SessionCard from './SessionCard.jsx';
import EventRibbon from './EventRibbon.jsx';
import SurveyPopup from './SurveyPopup.jsx';
import FilterPanel from './FilterPanel.jsx';
import TimeZoneSelector from './TimeZoneSelector.jsx';
import TabNavigation from './TabNavigation.jsx';
import { formatEventDate } from '../utils/eventDate.js';
import videoIcon from '../assets/OPSSWLearningDay_Video_.svg';
import './PreviewPanel.css';
import './Dashboard.css'; // Reuse Dashboard styles for authentic preview

const appLogo = '/applogo.svg';

const PreviewPanel = ({ eventId, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState('name');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('local');
  const [eventInfo, setEventInfo] = useState(null);
  const [quarterlyEvents, setQuarterlyEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('live');
  const [segmentHubLinks, setSegmentHubLinks] = useState({});
  const [videoTourUrl, setVideoTourUrl] = useState('https://connect.industrysoftware.automation.siemens.com/platform-tour');

  // Segment hub navigation - will be populated from settings
  const segmentHubLinksStatic = {
    'OPS-SW': '',
    GROW_PLM: '',
    LCS: '',
    ME: '',
    PES: '',
    SIM: '',
  };

  const handleHubSegmentChange = (e) => {
    const segment = e.target.value;
    const links = Object.keys(segmentHubLinks).length > 0 ? segmentHubLinks : segmentHubLinksStatic;
    if (segment && links[segment]) {
      window.open(links[segment], '_blank');
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (eventId) {
      loadPreviewData();
    }
  }, [eventId]);

  const loadPreviewData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch settings first
      const settings = await ApiService.getPublicSettings();
      const links = {
        'OPS-SW': settings.segment_hub_ops_sw_url || segmentHubLinksStatic['OPS-SW'],
        GROW_PLM: settings.segment_hub_grow_plm_url || segmentHubLinksStatic.GROW_PLM,
        LCS: settings.segment_hub_lcs_url || segmentHubLinksStatic.LCS,
        ME: settings.segment_hub_me_url || segmentHubLinksStatic.ME,
        PES: settings.segment_hub_pes_url || segmentHubLinksStatic.PES,
        SIM: settings.segment_hub_sim_url || segmentHubLinksStatic.SIM,
      };
      setSegmentHubLinks(links);
      
      if (settings.video_tour_url) {
        setVideoTourUrl(settings.video_tour_url);
      }
      
      const response = await AdminApiService.previewSessions(eventId);
      setSessions(response.data || []);
      
      // Get event info for ribbon
      const events = await ApiService.getQuarterlyEvents();
      setQuarterlyEvents(events);
      const event = events.find(e => e.id === eventId);
      setEventInfo(event);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError(err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering (exact same logic as Dashboard)
  const filteredSessions = sessions.filter(session => {
    // Segment filter
    if (selectedSegment && session.segment !== selectedSegment) {
      return false;
    }

    // Category filter
    if (selectedCategory && session.category !== selectedCategory) {
      return false;
    }

    // Search filter
    if (searchBy === 'name' && searchQuery) {
      const query = searchQuery.toLowerCase();
      return session.name.toLowerCase().includes(query) ||
             session.presenter.toLowerCase().includes(query);
    } else if (searchBy === 'time' && (startTime || endTime)) {
      if (!session.timeCET) return false;
      
      const [sessionStart] = session.timeCET.split('-');
      
      const toMinutes = (time) => {
        const [hours, mins] = time.split(':').map(Number);
        return hours * 60 + mins;
      };
      
      const sessionStartMins = toMinutes(sessionStart);
      
      if (startTime && endTime) {
        const startMins = toMinutes(startTime);
        const endMins = toMinutes(endTime);
        return sessionStartMins >= startMins && sessionStartMins <= endMins;
      } else if (startTime) {
        const startMins = toMinutes(startTime);
        return sessionStartMins >= startMins;
      } else if (endTime) {
        const endMins = toMinutes(endTime);
        return sessionStartMins <= endMins;
      }
    }

    return true;
  });

  // Time conversion for sidebar (exact same as Dashboard)
  const convertTimeForSidebar = (timeCET, sessionDate) => {
    try {
      if (!timeCET || typeof timeCET !== 'string') {
        return { display: timeCET || 'TBD', showDate: false };
      }

      const timeRange = timeCET.split('-');
      if (timeRange.length !== 2) {
        return { display: timeCET, showDate: false };
      }

      const [startTime] = timeRange;
      if (!startTime || !startTime.includes(':')) {
        return { display: timeCET, showDate: false };
      }

      const [hours, minutes] = startTime.split(':').map(h => h.trim());
      const cetTime = DateTime.fromObject(
        { hour: parseInt(hours), minute: parseInt(minutes || '0'), second: 0 },
        { zone: 'Europe/Paris' }
      );

      if (selectedTimezone === 'local') {
        const localTime = cetTime.setZone(DateTime.local().zoneName);
        const localDate = localTime.toFormat('dd MMM');
        const sessionCetDate = sessionDate
          ? DateTime.fromISO(sessionDate, { zone: 'Europe/Paris' }).toFormat('dd MMM')
          : null;
        const showDate = sessionCetDate && localDate !== sessionCetDate;
        return {
          display: localTime.toFormat('HH:mm'),
          showDate,
          date: showDate ? localDate : null,
        };
      }

      return { display: timeCET.split('-')[0], showDate: false };
    } catch (error) {
      console.error('Time conversion error:', error);
      return { display: timeCET.split('-')[0], showDate: false };
    }
  };

  // Group sessions by time for sidebar display
  const sessionsByTime = {};
  filteredSessions.forEach(session => {
    const timeKey = session.timeCET ? session.timeCET.split('-')[0] : 'TBD';
    if (!sessionsByTime[timeKey]) {
      sessionsByTime[timeKey] = {
        sessions: [],
        originalTime: timeKey,
        session: session
      };
    }
    sessionsByTime[timeKey].sessions.push(session);
  });

  const handleOverlayClick = (e) => {
    if (e.target.className === 'preview-panel-overlay') {
      onClose();
    }
  };

  return (
    <div className="preview-panel-overlay" onClick={handleOverlayClick}>
      <div className="preview-panel-modal-fullscreen">
        {/* Preview Header Banner */}
        <div className="preview-banner">
          <div className="preview-banner-content">
            <span className="preview-icon">👁️</span>
            <div className="preview-text">
              <strong>PREVIEW MODE</strong>
              <span className="preview-subtitle">Reviewing sessions before publishing to end users</span>
            </div>
          </div>
          <button className="btn-close-preview-banner" onClick={onClose}>
            ✖ Close Preview
          </button>
        </div>

        {loading && (
          <div className="preview-panel-loading">
            <div className="spinner"></div>
            <p>Loading preview...</p>
          </div>
        )}

        {error && (
          <div className="preview-panel-error">
            <p>❌ {error}</p>
            <button className="btn-retry" onClick={loadPreviewData}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="dashboard">
            {/* Full Dashboard Header */}
            <header className="dashboard-header">
              <div className="header-left">
                <img src={appLogo} alt="App Logo" className="app-logo-svg" />
                <div className="hub-dropdown-container">
                  <div className="hub-label">Segment Hub Link</div>
                  <select className="hub-select-hidden" onChange={handleHubSegmentChange}>
                    <option value="" hidden></option>
                  <option value="OPS-SW">OPS-SW</option>
                  <option value="GROW_PLM">Grow@PLM</option>
                  <option value="LCS">LCS</option>
                  <option value="ME">ME</option>
                  <option value="PES">PES</option>
                  <option value="SIM">SIM</option>
                  </select>
                </div>
              </div>

              {/* Center Search Bar */}
              <div className="header-center">
                <div className="search-bar">
                  <select 
                    value={searchBy} 
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      setSearchQuery('');
                      setStartTime('');
                      setEndTime('');
                    }}
                  >
                    <option value="name">Name</option>
                    <option value="time">Time</option>
                  </select>
                  {searchBy === 'name' ? (
                    <input
                      type="text"
                      placeholder="Session Name or Presenter Name"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  ) : (
                    <>
                      <input
                        type="time"
                        placeholder="Start Time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="time-input"
                      />
                      <span className="time-separator">to</span>
                      <input
                        type="time"
                        placeholder="End Time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="time-input"
                      />
                    </>
                  )}
                  <button>🔍</button>
                </div>
              </div>

              {/* Right Section */}
              <div className="header-right">
                {videoTourUrl && (
                  <a 
                    href={videoTourUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="video-tour-link" 
                    title="Platform Video Tour"
                  >
                    <img src={videoIcon} alt="Video Tour" className="video-icon-svg" />
                  </a>
                )}
                <div className="siemens-logo">SIEMENS</div>
              </div>
            </header>

            {/* Tab Navigation */}
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              quarterlyEvents={quarterlyEvents}
              selectedEventDate={eventInfo?.event_date}
              onEventDateChange={() => {}}
            />

            {/* Segment Filter Bar */}
            <div className="segment-bar">
              <div
                className={selectedSegment === null ? 'active' : ''}
                onClick={() => setSelectedSegment(null)}
              >
                All Segments
              </div>
              <div
                className={selectedSegment === 'OPS-SW' ? 'active' : ''}
                onClick={() => setSelectedSegment('OPS-SW')}
              >
                OPS-SW
              </div>
              <div
                className={selectedSegment === 'GROW_PLM' ? 'active' : ''}
                onClick={() => setSelectedSegment('GROW_PLM')}
              >
                Grow@PLM
              </div>
              <div
                className={selectedSegment === 'LCS' ? 'active' : ''}
                onClick={() => setSelectedSegment('LCS')}
              >
                LCS
              </div>
              <div
                className={selectedSegment === 'ME' ? 'active' : ''}
                onClick={() => setSelectedSegment('ME')}
              >
                ME
              </div>
              <div
                className={selectedSegment === 'PES' ? 'active' : ''}
                onClick={() => setSelectedSegment('PES')}
              >
                PES
              </div>
              <div
                className={selectedSegment === 'SIM' ? 'active' : ''}
                onClick={() => setSelectedSegment('SIM')}
              >
                SIM
              </div>
            </div>

            {/* Filter Instruction */}
            <div className="filter-instruction">
              Use the colored category badges below to filter sessions
            </div>

            {/* Category Filter */}
            <FilterPanel
              selectedCategory={selectedCategory}
              onCategorySelect={setSelectedCategory}
            />

            {/* Event Ribbon */}
            {eventInfo && (
              <EventRibbon 
                title={eventInfo.event_name || 'Learning Day'} 
                date={formatEventDate(eventInfo.event_date)}
              />
            )}

            {/* Timezone Selector */}
            <TimeZoneSelector
              selectedTimezone={selectedTimezone}
              onTimezoneChange={setSelectedTimezone}
            />

            {/* Main Content Grid */}
            <main className="content-grid">
              {filteredSessions.length === 0 ? (
                <div className="no-results">
                  <h2>📭 No sessions to display</h2>
                  <p>
                    {sessions.length === 0 
                      ? 'No sessions have been imported for this event yet.' 
                      : 'Try adjusting the segment, category, or search filters above.'}
                  </p>
                </div>
              ) : (
                Object.entries(sessionsByTime).map(([timeKey, timeGroup]) => {
                  const convertedTime = convertTimeForSidebar(
                    timeGroup.session.timeCET,
                    timeGroup.session.date
                  );
                  return (
                    <div key={timeKey} className="time-block">
                      <div className="time-sidebar">
                        {convertedTime.display}
                        {convertedTime.showDate && (
                          <div className="time-date">{convertedTime.date}</div>
                        )}
                      </div>
                      <div className="session-container">
                        {timeGroup.sessions.map(session => (
                          <SessionCard
                            key={session.id}
                            session={session}
                            timezone={selectedTimezone}
                            isArchived={false}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </main>

            {/* Survey Popup */}
            <SurveyPopup />
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewPanel;
